import React, { useEffect, useState } from "react";
import { X, Receipt, Clock, AlertCircle, RefreshCw } from "lucide-react";
import { getCustomerPayments, getCustomerPaymentType } from "../api/payments.api";
import { formatDateTime24 } from "../../utils/formatTime24";

const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '---';
  const numericAmount = Number(amount);
  const formatted = Number.isNaN(numericAmount) ? amount : numericAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${String(currency).toUpperCase()} ${formatted}` : formatted;
};

const formatDate = (value) => {
  if (!value) return '---';
  return formatDateTime24(value, { fallback: '---' });
};

const PAYMENT_TYPE_LABEL = {
  mensual: 'Mensual',
  paquete_dias: 'Paquete de días',
};

const CustomerPaymentsModal = ({ isOpen, onClose, customerId, customerName }) => {
  const [payments, setPayments] = useState([]);
  const [paymentType, setPaymentType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen || !customerId) return;

    let ignore = false;

    const fetchData = async () => {
      setLoading(true);
      setError(false);
      setPaymentType(null);
      try {
        const data = await getCustomerPayments(customerId);
        if (!ignore) setPayments(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!ignore) {
          setError(true);
          setPayments([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }

      try {
        const type = await getCustomerPaymentType(customerId);
        if (!ignore) setPaymentType(type);
      } catch (err) {
        // Customer has no payments yet, or classification is unavailable: no badge, not an error.
        if (!ignore) setPaymentType(null);
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, [isOpen, customerId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">

        {/* Header */}
        <div className="p-5 border-b dark:border-gray-700 flex justify-between items-center bg-blue-50 dark:bg-gray-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <Receipt className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 dark:text-white leading-tight">
                Historial de Pagos
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{customerName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {paymentType && (
          <div className="px-5 pt-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
              {PAYMENT_TYPE_LABEL[paymentType.type] || paymentType.type}
            </span>
          </div>
        )}

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-4 custom-scrollbar">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <RefreshCw className="animate-spin text-blue-500 w-8 h-8" />
              <p className="text-gray-500 text-sm font-medium">Obteniendo pagos...</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="text-red-400 mb-3" size={32} />
              <p className="text-red-600 dark:text-red-400 font-medium text-sm">No se pudieron obtener los pagos</p>
            </div>
          )}

          {!loading && !error && payments.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700/50 rounded-full flex items-center justify-center mb-4">
                <Receipt className="w-8 h-8 text-gray-300 dark:text-gray-600" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-medium">Sin pagos registrados</p>
              <p className="text-xs text-gray-400">Este cliente todavía no tiene pagos cargados</p>
            </div>
          )}

          {!loading && !error && payments.length > 0 && (
            <div className="space-y-3">
              {payments.map((payment) => {
                const data = payment.data || {};
                return (
                  <div
                    key={payment.id}
                    className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-600 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                          {formatAmount(data.amount, data.currency)}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                          <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                            {data.paymentStatus || 'sin estado'}
                          </span>
                          {data.manualCreated && (
                            <span className="inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                              Manual
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400 font-mono font-bold">
                            <Clock className="w-3 h-3" />
                            {formatDate(data.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-sm font-bold shadow-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerPaymentsModal;
